import React, { useState, useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "react-query";
import {
  CssBaseline,
  Container,
  Box,
  Typography,
  AppBar,
  Toolbar,
  Paper,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { StyledEngineProvider } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import LockIcon from "@mui/icons-material/Lock";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

// Import custom theme and styles
import theme from "./styles/theme";
import GlobalStyles from "./styles/GlobalStyles";

// Import auth
import { AuthProvider, useAuth } from "./context/AuthContext";

// Import components
import LoginPage from "./components/LoginPage";
import UrlCreationModal from "./components/UrlCreationModal";
import UrlTable from "./components/UrlTable";
import ChangePasswordModal from "./components/ChangePasswordModal";

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

// Constants for domains
const APP_DOMAIN = "app.richs.my";
const LINK_DOMAIN = "link.richs.my";
const REDIRECT_DOMAIN = "richs.com.my";

// Check if we're in development or production
const isDevelopment = () => {
  return !window.location.hostname.includes("richs.my");
};

// Determine the current domain type
const getCurrentDomainType = () => {
  const hostname = window.location.hostname;
  const path = window.location.pathname;

  // Special case for CodeSandbox testing with /debug/
  if (path.startsWith("/debug/")) {
    console.log("Debug mode detected, treating as app domain");
    return "app";
  }

  // In development mode, treat as app domain unless explicitly using link paths
  if (isDevelopment()) {
    if (path.startsWith("/link")) {
      return "link";
    } else if (path.length > 1 && !path.includes("/")) {
      // Direct shortcode in development
      return "link";
    } else {
      return "app"; // Default to app in development
    }
  }

  // In production, check actual hostname
  if (hostname.includes(APP_DOMAIN)) {
    return "app";
  } else if (hostname.includes(LINK_DOMAIN)) {
    return "link";
  }

  return "app"; // Default fallback to app
};

/**
 * Dashboard Component
 * The main app content - only shown when user is authenticated
 */
function Dashboard({ domainInfo }) {
  const { user, logout } = useAuth();

  // State for managing the URL creation modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [isLoading, setIsLoading] = useState(false);

  // State for user menu
  const [anchorEl, setAnchorEl] = useState(null);
  const userMenuOpen = Boolean(anchorEl);

  // State for change password modal
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Set document title
  useEffect(() => {
    document.title = "RICH PRODUCTS MALAYSIA SDN BHD";
  }, []);

  // Use provided domain info or determine it if not provided
  const currentDomainInfo = domainInfo || {
    type: getCurrentDomainType(),
    isApp: getCurrentDomainType() === "app",
    isLink: getCurrentDomainType() === "link",
    isDevelopment: isDevelopment(),
    domains: {
      app: isDevelopment()
        ? `${window.location.origin}`
        : `https://${APP_DOMAIN}`,
      link: isDevelopment()
        ? `${window.location.origin}`
        : `https://${LINK_DOMAIN}`,
      redirect: `https://${REDIRECT_DOMAIN}`,
    },
  };

  // Log domain info for debugging
  useEffect(() => {
    console.log("Current domain info:", currentDomainInfo);
  }, [currentDomainInfo]);

  // If we're on the link domain and not in debug mode, don't render the UI
  if (
    currentDomainInfo.isLink &&
    !window.location.pathname.startsWith("/debug/")
  ) {
    console.log("Link domain detected, not rendering dashboard UI");
    return (
      <div style={{ display: "none" }}>
        {/* Empty container for redirect handling */}
      </div>
    );
  }

  // Handlers for modal
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  // Handle URL creation success
  const handleUrlCreated = (data) => {
    setIsModalOpen(false);
    setNotification({
      show: true,
      message: "URL has been successfully shortened!",
      type: "success",
    });

    // Invalidate and refetch URLs to update the table
    queryClient.invalidateQueries("urls");
  };

  // Handle URL creation error
  const handleUrlCreationError = (error) => {
    setNotification({
      show: true,
      message: `Failed to create shortened URL: ${error.message}`,
      type: "error",
    });
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, show: false }));
  };

  // User menu handlers
  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleChangePassword = () => {
    handleUserMenuClose();
    setChangePasswordOpen(true);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  return (
    <>
      {/* App Layout */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        {/* App Bar with Logo and User Menu */}
        <AppBar position="static" color="primary">
          <Toolbar
            sx={{
              justifyContent: "space-between",
              position: "relative",
            }}
          >
            {/* Spacer for centering */}
            <Box sx={{ width: 160 }} />

            {/* Centered Logo */}
            <Box
              component="img"
              src="https://wiki.richproductsmy.kitchen/wp-content/uploads/2025/03/richs_logo_lockup_horiz.png"
              alt="Rich Products Logo"
              sx={{
                height: "80px",
                my: 1,
                objectFit: "contain",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            />

            {/* User Menu - Right Side */}
            <Button
              onClick={handleUserMenuOpen}
              sx={{
                color: "white",
                textTransform: "none",
                borderRadius: "8px",
                padding: "6px 12px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
              startIcon={<AccountCircleIcon />}
              endIcon={<KeyboardArrowDownIcon />}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  display: { xs: "none", sm: "block" },
                }}
              >
                {user?.displayName || "User"}
              </Typography>
            </Button>

            <Menu
              anchorEl={anchorEl}
              open={userMenuOpen}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              PaperProps={{
                sx: {
                  minWidth: 200,
                  mt: 1,
                  borderRadius: "8px",
                  boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              {/* User info header */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {user?.displayName || "User"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "12px" }}>
                  {user?.role || "admin"}
                </Typography>
              </Box>

              <Divider />

              {/* Change Password */}
              <MenuItem onClick={handleChangePassword}>
                <ListItemIcon>
                  <LockIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Password</ListItemText>
              </MenuItem>

              <Divider />

              {/* Logout */}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>
                  <Typography color="error.main">Log Out</Typography>
                </ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              display: "flex",
              flexDirection: "column",
              minHeight: "70vh",
            }}
          >
            {/* Header with Create Button */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={3}
            >
              <Typography variant="h2" component="h2">
                Your Shortened URLs
              </Typography>

              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenModal}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <CircularProgress
                      size={20}
                      sx={{ mr: 1 }}
                      color="inherit"
                    />
                    Creating...
                  </>
                ) : (
                  "Create New URL"
                )}
              </Button>
            </Box>

            {/* URL Table */}
            <UrlTable domainInfo={currentDomainInfo} />
          </Paper>
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{
            py: 3,
            px: 2,
            mt: "auto",
            backgroundColor: theme.palette.grey[100],
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
            >
              © {new Date().getFullYear()} RICH PRODUCTS MALAYSIA SDN BHD
              {currentDomainInfo.isDevelopment && (
                <span> | Development Mode</span>
              )}
            </Typography>
          </Container>
        </Box>
      </Box>

      {/* URL Creation Modal */}
      <UrlCreationModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onUrlCreated={handleUrlCreated}
        onError={handleUrlCreationError}
        setIsLoading={setIsLoading}
        domainInfo={currentDomainInfo}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      {/* Notifications */}
      <Snackbar
        open={notification.show}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}

/**
 * AppContent Component
 * Decides whether to show LoginPage or Dashboard based on auth state
 */
function AppContent({ domainInfo }) {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking session
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress color="primary" size={48} />
      </Box>
    );
  }

  // Not authenticated → show login page
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Authenticated → show dashboard
  return <Dashboard domainInfo={domainInfo} />;
}

/**
 * App Component (root)
 * Wraps everything in providers
 */
function App({ domainInfo }) {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          {/* Apply MUI's CssBaseline for consistent baseline styles */}
          <CssBaseline />

          {/* Apply our custom global styles */}
          <GlobalStyles />

          {/* Auth Provider wraps everything */}
          <AuthProvider>
            <AppContent domainInfo={domainInfo} />
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

export default App;
